import { roleAtLeast } from '@forge/shared';
import {
  Database,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  PanelLeftClose,
  ScrollText,
  Settings2,
  Share2,
  Users,
  X,
} from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { useProjectRole } from '../lib/auth';
import { SURFACE, type Surface } from '../lib/surface';
import { useProject } from '../lib/queries';
import { useProjectRealtime } from '../lib/useRealtime';
import { useSidebarStore } from '../lib/sidebarStore';
import { EmptyState, IconButton, Skeleton } from './ui';
import { cn } from '../lib/cn';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
  minRole?: 'viewer' | 'editor' | 'admin' | 'owner';
  /** Surfaces this entry belongs to. Absent means both. */
  only?: Surface;
}

export default function ProjectLayout() {
  const { projectId } = useParams();
  const location = useLocation();
  const { data: project, isLoading, error } = useProject(projectId);
  const { role } = useProjectRole(projectId);
  const { isOpen, isMobileOpen, closeMobile, toggleDesktop } = useSidebarStore();

  useProjectRealtime(projectId);

  // Close mobile drawer on route navigation
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  // Close mobile drawer on Escape key
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileOpen) closeMobile();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobileOpen, closeMobile]);

  // Two filters apply. Role decides what a person may see at all; surface
  // decides what this build ships — the viewing app has no route for the
  // builder, so offering the link would be a dead end.
  const allItems: NavItem[] = [
    { to: '', label: 'Overview', icon: <LayoutGrid className="size-4" />, end: true, minRole: 'viewer' },
    { to: 'endpoints', label: 'APIs', icon: <Share2 className="size-4" />, minRole: 'viewer', only: 'admin' },
    { to: 'dashboards', label: 'Dashboards', icon: <LayoutDashboard className="size-4" />, minRole: 'viewer' },
    { to: 'datasources', label: 'Data', icon: <Database className="size-4" />, minRole: 'editor', only: 'admin' },
    { to: 'logs', label: 'Logs', icon: <ScrollText className="size-4" />, minRole: 'editor', only: 'admin' },
    { to: 'settings', label: 'Settings', icon: <Settings2 className="size-4" />, minRole: 'editor', only: 'admin' },
  ];
  const items = allItems.filter((item) => !item.only || item.only === SURFACE);

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 flex-row">
        <aside
          className={cn(
            'hidden md:block shrink-0 border-r border-ink-850 p-3 transition-all duration-200',
            isOpen ? 'w-52' : 'w-0 border-r-0 overflow-hidden p-0'
          )}
        >
          <div className="w-52 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </aside>
        <div className="flex-1 p-4 sm:p-6">
          <Skeleton className="h-8 w-48 sm:w-64" />
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <EmptyState
        icon={<Users className="size-5" />}
        title="Project unavailable"
        description="It may have been deleted, or your access was removed. Head back to the project list."
      />
    );
  }

  const visibleItems = items.filter((item) => !item.minRole || roleAtLeast(role, item.minRole));

  return (
    <div className="flex h-full min-h-0 flex-row relative">
      {/* Mobile sidebar backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/60 backdrop-blur-xs transition-opacity duration-200 md:hidden',
          isMobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={closeMobile}
      />

      {/* Mobile sidebar drawer (slides in/out from the left side) */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-ink-800 bg-ink-900 shadow-2xl transition-transform duration-200 ease-in-out md:hidden',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-ink-800 px-3.5">
          <div className="flex items-center gap-2">
            <div className="flex size-5.5 items-center justify-center rounded bg-brand-500 text-[10px] font-bold text-white">
              F
            </div>
            <span className="truncate text-xs font-semibold text-ink-200 uppercase tracking-wider">Menyu</span>
          </div>
          <IconButton label="Yopish" variant="ghost" size="xs" onClick={closeMobile}>
            <X className="size-4" />
          </IconButton>
        </div>

        <nav className="flex-1 space-y-1 p-2.5 overflow-y-auto">
          {visibleItems.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              end={item.end}
              onClick={closeMobile}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-xs font-medium transition-colors',
                  isActive
                    ? 'bg-ink-800 text-ink-100 shadow-xs'
                    : 'text-ink-400 hover:bg-ink-850 hover:text-ink-200',
                )
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-ink-800 p-3">
          <p className="text-[10px] font-medium tracking-wide text-ink-500 uppercase">Public base URL</p>
          <code className="mt-1 block truncate rounded-md bg-ink-950 px-2 py-1.5 font-mono text-[10px] text-ink-300">
            /api/v1/{project.slug}
          </code>
          <div className="mt-2.5 flex items-center gap-1.5 text-[10px] text-ink-500">
            <KeyRound className="size-3" />
            You are {role ?? 'not a member'}
          </div>
        </div>
      </aside>

      {/* Desktop sidebar (collapsible to the side) */}
      <aside
        className={cn(
          'hidden md:flex shrink-0 flex-col border-r border-ink-850 bg-ink-950 transition-all duration-200 ease-in-out',
          isOpen ? 'w-52' : 'w-0 border-r-0 overflow-hidden'
        )}
      >
        <div className="w-52 flex flex-col flex-1 h-full min-h-0">
          <nav className="flex-1 space-y-0.5 p-2.5 overflow-y-auto">
            {visibleItems.map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-ink-800 text-ink-100'
                      : 'text-ink-400 hover:bg-ink-850 hover:text-ink-200',
                  )
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="border-t border-ink-850 p-3 space-y-2.5">
            <div>
              <p className="text-[10px] font-medium tracking-wide text-ink-500 uppercase">Public base URL</p>
              <code className="mt-1.5 block truncate rounded-md bg-ink-900 px-2 py-1.5 font-mono text-[10px] text-ink-300">
                /api/v1/{project.slug}
              </code>
              <div className="mt-2.5 flex items-center gap-1.5 text-[10px] text-ink-500">
                <KeyRound className="size-3" />
                You are {role ?? 'not a member'}
              </div>
            </div>

            <button
              type="button"
              onClick={toggleDesktop}
              className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-xs text-ink-500 hover:bg-ink-900 hover:text-ink-200 transition-colors"
              title="Sidebar-ni yonga yopish"
            >
              <span className="text-[11px]">Sidebar-ni yopish</span>
              <PanelLeftClose className="size-3.5" />
            </button>
          </div>
        </div>
      </aside>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        <Outlet />
      </div>
    </div>
  );
}
